import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Mission, Priority, MissionStatus } from '../types';
import { formatMissionType, formatMissionStatus, formatTimeAgo } from '../utils/formatUtils';
import clsx from 'clsx';

interface MissionTableProps {
  missions: Mission[];
}

const MissionTable: React.FC<MissionTableProps> = ({ missions }) => {
  const navigate = useNavigate();

  const getPriorityIndicator = (priority: Priority) => {
    switch (priority) {
      case Priority.CRITICAL:
        return 'bg-red-500';
      case Priority.URGENT:
        return 'bg-yellow-500';
      case Priority.ROUTINE:
        return 'bg-green-500';
      default:
        return 'bg-gray-400';
    }
  };

  const getElapsedTime = (mission: Mission) => {
    if (!mission.timeReceived) return '--';
    const start = new Date(mission.timeReceived);
    const isTerminal = [MissionStatus.CLOSED, MissionStatus.MISSION_COMPLETE, MissionStatus.CANCELLED].includes(mission.status as MissionStatus);
    const end = isTerminal
      ? (mission.dateClosed ? new Date(mission.dateClosed) : mission.timeMissionComplete ? new Date(mission.timeMissionComplete) : new Date())
      : new Date();
    const diff = end.getTime() - start.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    if (hours >= 24) {
      const days = Math.floor(hours / 24);
      return `${days}d ${hours % 24}h`;
    }
    return `${hours}h ${minutes}m`;
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mission</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Priority</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Base</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Patients</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Received</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Elapsed</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {missions.map((mission) => (
              <tr
                key={mission.id}
                onClick={() => navigate(`/mission/${mission.id}`)}
                className="hover:bg-gray-50 cursor-pointer transition-colors"
              >
                <td className="px-4 py-3 whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm text-gray-900">{mission.missionNumber}</span>
                    {mission.isTestTraining && (
                      <span className="text-[10px] px-1.5 py-0.5 bg-gray-200 text-gray-500 rounded">TEST</span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <span className="text-xs font-medium">{formatMissionStatus(mission.status)}</span>
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    <span className={clsx('w-2.5 h-2.5 rounded-full', getPriorityIndicator(mission.priority as Priority))} />
                    <span className="text-sm">{mission.priority}</span>
                  </div>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                  {formatMissionType(mission.missionType)}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                  {mission.primaryBase?.code || '--'}
                </td>
                <td className="px-4 py-3 text-sm text-gray-600 max-w-[200px] truncate">
                  {mission.sceneLocation || '--'}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 text-center">
                  {mission.patients?.length ?? mission.patientCount ?? 0}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                  {formatTimeAgo(mission.timeReceived)}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                  {getElapsedTime(mission)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MissionTable;
